import { useState, type FormEvent } from 'react';
import { Pencil } from 'lucide-react';
import { formatPercent, formatPricePerKWh, type TariffPrices, type UpdateTariffPrices } from '@ste/shared';
import { useTariffs, useUpdateTariffs } from '../../api/tariffs.ts';
import { Banner, Button, Card, Spinner } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { CommandError } from '../charging/CommandError.tsx';
import { formatZone, parseCt, toCtInput, validPrice } from './prices.ts';

const inputClass = 'mt-1 block min-h-11 w-full rounded-lg border border-border bg-surface-2 px-3 text-text';

/** the customer's prices (ADR-0018): set up by the installer, the owner may change the amounts */
export function PricesCard() {
  const tariffs = useTariffs();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const data = tariffs.data;

  return (
    <Card
      title={t('prices.title')}
      action={
        data?.editable && !editing ? (
          <Button
            variant="ghost"
            onClick={() => {
              setSaved(false);
              setEditing(true);
            }}
          >
            <Pencil size={16} /> {t('prices.edit')}
          </Button>
        ) : undefined
      }
    >
      {tariffs.isLoading && <Spinner label={t('common.loading')} />}
      {tariffs.isError && <CommandError error={tariffs.error} />}
      {data && !editing && (
        <div className="space-y-3">
          {saved && <Banner tone="accent" title={t('prices.saved')} />}
          <PriceList prices={data} />
        </div>
      )}
      {data && editing && (
        <PricesForm
          prices={data}
          onClose={(done) => {
            setEditing(false);
            setSaved(done);
          }}
        />
      )}
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium tabular-nums">{value}</dd>
    </div>
  );
}

function PriceList({ prices }: { prices: TariffPrices }) {
  const { grid, feedIn } = prices;
  if (!grid && feedIn === null) return <p className="text-muted">{t('prices.none')}</p>;
  return (
    <dl className="divide-y divide-border">
      {grid?.kind === 'fixed' && (
        <>
          <Row label={t('prices.grid')} value={formatPricePerKWh(grid.price)} />
          {grid.zones.map((zone, i) => (
            <Row
              key={i}
              label={t('prices.zone', { zone: formatZone(zone) })}
              value={formatPricePerKWh(zone.price)}
            />
          ))}
        </>
      )}
      {grid?.kind === 'dynamic' && (
        <>
          <Row label={t('prices.grid')} value={t('prices.dynamic', { provider: grid.provider })} />
          {grid.charges !== undefined && (
            <Row label={t('prices.charges')} value={formatPricePerKWh(grid.charges)} />
          )}
          {grid.tax !== undefined && <Row label={t('prices.tax')} value={formatPercent(grid.tax * 100)} />}
        </>
      )}
      {feedIn !== null && <Row label={t('prices.feedIn')} value={formatPricePerKWh(feedIn)} />}
    </dl>
  );
}

function PricesForm({ prices, onClose }: { prices: TariffPrices; onClose(saved: boolean): void }) {
  const update = useUpdateTariffs();
  const grid = prices.grid;
  const [price, setPrice] = useState(grid?.kind === 'fixed' ? toCtInput(grid.price) : '');
  const [zonePrices, setZonePrices] = useState(
    grid?.kind === 'fixed' ? grid.zones.map((z) => toCtInput(z.price)) : [],
  );
  const [charges, setCharges] = useState(grid?.kind === 'dynamic' ? toCtInput(grid.charges) : '');
  const [feedIn, setFeedIn] = useState(toCtInput(prices.feedIn ?? undefined));

  const body: UpdateTariffPrices = {};
  let valid = true;
  const read = (text: string, required: boolean, max = 2) => {
    const value = parseCt(text);
    if (!validPrice(value, max) || (required && value === undefined)) valid = false;
    return value;
  };
  if (!grid || grid.kind === 'fixed') {
    const gridPrice = read(price, grid?.kind === 'fixed');
    const zones = zonePrices.map((z) => read(z, true));
    if (gridPrice !== undefined || zones.length > 0)
      body.grid = {
        ...(gridPrice !== undefined ? { price: gridPrice } : {}),
        ...(zones.length > 0 ? { zonePrices: zones as number[] } : {}),
      };
  } else if (grid.charges !== undefined) {
    const value = read(charges, true);
    if (value !== undefined) body.grid = { charges: value };
  }
  const feedInValue = read(feedIn, false, 1);
  if (feedInValue !== undefined) body.feedIn = feedInValue;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) return;
    update.mutate(body, { onSuccess: () => onClose(true) });
  };

  const field = (label: string, value: string, set: (v: string) => void, key: string) => (
    <label key={key} className="block max-w-xs">
      <span className="text-sm text-muted">{label}</span>
      <span className="relative block">
        <input
          className={`${inputClass} pr-20`}
          inputMode="decimal"
          value={value}
          disabled={update.isPending}
          onChange={(e) => set(e.target.value)}
        />
        <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 pt-1 text-sm text-muted">
          ct/kWh
        </span>
      </span>
    </label>
  );

  return (
    <form onSubmit={submit} className="space-y-4">
      {(!grid || grid.kind === 'fixed') && field(t('prices.grid'), price, setPrice, 'price')}
      {grid?.kind === 'fixed' &&
        grid.zones.map((zone, i) =>
          field(
            t('prices.zone', { zone: formatZone(zone) }),
            zonePrices[i] ?? '',
            (v) => setZonePrices(zonePrices.map((z, j) => (j === i ? v : z))),
            `zone${i}`,
          ),
        )}
      {grid?.kind === 'dynamic' && (
        <p className="text-muted">{t('prices.dynamic', { provider: grid.provider })}</p>
      )}
      {grid?.kind === 'dynamic' &&
        grid.charges !== undefined &&
        field(t('prices.charges'), charges, setCharges, 'charges')}
      {field(t('prices.feedIn'), feedIn, setFeedIn, 'feedIn')}
      <p className="text-sm text-muted">{t('prices.restartHint')}</p>
      {update.isError && <CommandError error={update.error} />}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!valid || update.isPending}>
          {update.isPending ? t('prices.saving') : t('common.save')}
        </Button>
        <Button type="button" variant="ghost" disabled={update.isPending} onClick={() => onClose(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
